/**
 * @file {@link https://www.rfc-editor.org/rfc/rfc9110#section-12 RFC 9110 12 Content Negotiation}
 */

import {
  Attribute,
  MediaType,
  Parameters,
  Type as TopType,
  Type,
} from "@redotech/media-type";
import {
  FieldName,
  FieldType,
  fieldNameValueFormat,
  listFieldFormat,
  stringFieldValueFormat,
} from "./fields";
import { optionalWhitespaceRead, quotableRead, tokenRead } from "./syntax";

export type MediaRange = MediaRange.All | MediaRange.Top | MediaRange.Type;

export namespace MediaRange {
  export const ALL = Symbol("all");
  export const TOP = Symbol("top");
  export const TYPE = Symbol("type");

  export interface All {
    type: typeof ALL;
    parameters: Parameters;
  }

  export interface Top {
    type: typeof TOP;
    parameters: Parameters;
    value: TopType;
  }

  export interface Type {
    type: typeof TYPE;
    value: MediaType;
  }
}

function mediaRangeRead(
  string: string,
  start: number,
): { end: number; value: MediaRange } {
  const topResult = tokenRead(string, start);
  start = topResult.end;
  if (string[start] !== "/") {
    throw new Error(`Expected / at ${start}`);
  }
  start += "/".length;
  const subResult = tokenRead(string, start);
  start = subResult.end;
  let parameters = Parameters.EMPTY;
  while (true) {
    const end = optionalWhitespaceRead(string, start);
    if (string[end] !== ";") {
      break;
    }
    start = end + ";".length;
    start = optionalWhitespaceRead(string, start);
    const nameResult = tokenRead(string, start);
    start = nameResult.end;
    if (string[start] !== "=") {
      throw new Error(`Expected = at ${start}`);
    }
    start += "=".length;
    const valueResult = quotableRead(string, start);
    start = valueResult.end;
    parameters = parameters.set(
      new Attribute(nameResult.value),
      valueResult.value,
    );
  }
  if (topResult.value === "*") {
    if (subResult.value !== "*") {
      throw new Error("Invalid media range");
    }
    return { end: start, value: { type: MediaRange.ALL, parameters } };
  }
  const type = new Type(topResult.value);
  if (subResult.value === "*") {
    return {
      end: start,
      value: { type: MediaRange.TOP, value: type, parameters },
    };
  }
  return {
    end: start,
    value: {
      type: MediaRange.TYPE,
      value: type.subtype(subResult.value).withParameters(parameters),
    },
  };
}

function mediaRangeWrite(value: MediaRange): string {
  switch (value.type) {
    case MediaRange.ALL:
      return "*/*";
    case MediaRange.TOP:
      return `${value.value}/*`;
    case MediaRange.TYPE:
      return String(value.value);
  }
}

export interface WeightedMediaRange {
  range: MediaRange;
  quality: number;
}

export function weightedMediaRange(
  range: MediaRange,
  quality = 1,
): WeightedMediaRange {
  return { range, quality };
}

const qualityAttribute = new Attribute("q");

function weightedMediaRangeRead(
  string: string,
  start: number,
): { end: number; value: WeightedMediaRange } {
  const rangeResult = mediaRangeRead(string, start);
  start = rangeResult.end;
  let range = rangeResult.value;
  let qualityString: string | undefined;
  switch (range.type) {
    case MediaRange.ALL:
      qualityString = range.parameters.get(qualityAttribute);
      break;
    case MediaRange.TOP:
      qualityString = range.parameters.get(qualityAttribute);
      break;
    case MediaRange.TYPE:
      qualityString = range.value.parameters.get(qualityAttribute);
      range = {
        type: MediaRange.TYPE,
        value: range.value.withParameters(
          range.value.parameters.set(qualityAttribute, undefined),
        ),
      };
      break;
  }
  const quality = qualityString ? +qualityString : 1;
  if (quality < 0 || 1 < quality) {
    throw new Error(`Bad quality value: ${qualityString}`);
  }
  return { end: start, value: { range, quality: quality } };
}

function weightedMediaRangeWrite(value: WeightedMediaRange) {
  let result = mediaRangeWrite(value.range);
  if (value.quality !== 1) {
    const qualityText = value.quality.toFixed(3).replace(/\.?0+$/, "") || "0";
    result += `; ${qualityAttribute}=${qualityText}`;
  }
  return result;
}

/**
 * @see {@link https://www.rfc-editor.org/rfc/rfc9110#section-12.5.1 RFC 9110 12.5.1 Accept}
 */
export const AcceptField = new FieldType(new FieldName("Accept"), {
  read(fieldValue: string[]) {
    const ranges = [];
    for (const value of fieldValue) {
      for (let start = 0; start < fieldValue.length; ) {
        const result = weightedMediaRangeRead(value, start);
        start = result.end;
        ranges.push(result.value);
      }
    }
    return ranges;
  },
  write(value: WeightedMediaRange[]) {
    return value.length ? [value.map(weightedMediaRangeWrite).join(", ")] : [];
  },
});

/**
 * Accept-Charset
 * @see {@link https://www.rfc-editor.org/rfc/rfc9110#section-12.5.2 RFC 9110 12.5.2 Accept-Charset}
 */
export const AcceptCharsetField = new FieldType(
  new FieldName("Accept-Charset"),
  listFieldFormat(stringFieldValueFormat),
);

/**
 * Accept-Encoding
 * @see {@link https://www.rfc-editor.org/rfc/rfc9110#section-12.5.3 RFC 9110 12.5.3 Accept-Encoding}
 */
export const AcceptEncodingField = new FieldType(
  new FieldName("Accept-Encoding"),
  listFieldFormat(stringFieldValueFormat),
);

/**
 * Accept-Language
 * @see {@link https://www.rfc-editor.org/rfc/rfc9110#section-12.5.4 RFC 9110 12.5.4 Accept-Language}
 */
export const AcceptLanguageField = new FieldType(
  new FieldName("Accept-Language"),
  listFieldFormat(stringFieldValueFormat),
);

/**
 * Vary
 * @see {@link https://www.rfc-editor.org/rfc/rfc9110#section-12.5.5 RFC 9110 12.5.5 Var}
 */
export const VaryField = new FieldType(
  new FieldName("Vary"),
  listFieldFormat(fieldNameValueFormat),
);
